import { useRef, useState, type FormEvent } from "react";
import {
  getBookshelfView,
  streamSearchBooks,
  type BookSource,
  type BookshelfViewItem,
  type ContentKind,
  type SearchResult,
  type UserSpace,
} from "../api";
import { sortBookshelfView } from "../reader/reader-library";

interface BookshelfWorkspaceOptions {
  user: UserSpace | null;
  activeKind: ContentKind;
  setBusy: (busy: boolean) => void;
  setMessage: (message: string) => void;
}

export function useBookshelfWorkspace({
  user,
  activeKind,
  setBusy,
  setMessage,
}: BookshelfWorkspaceOptions) {
  const [bookshelf, setBookshelf] = useState<BookshelfViewItem[]>([]);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [searchMessage, setSearchMessage] = useState("输入书名或作者后开始搜索");
  const [searching, setSearching] = useState(false);
  const [selectedSourceByBook, setSelectedSourceByBook] = useState<Record<string, string>>({});
  // 用递增 token 忽略上一轮搜索晚到的结果
  const searchTokenRef = useRef(0);
  const currentSearchAbortControllerRef = useRef<AbortController | null>(null);

  async function loadBookshelf(userId: string, kind = activeKind) {
    const nextBookshelf = sortBookshelfView(await getBookshelfView(userId, kind));
    setBookshelf(nextBookshelf);
    return nextBookshelf;
  }

  async function searchByKeyword(keyword: string, kind = activeKind) {
    const normalizedKeyword = keyword.trim();
    if (!user || !normalizedKeyword) return [];

    abortCurrentSearch({ invalidate: true, showMessage: false });
    setQuery(normalizedKeyword);
    setBusy(true);
    setSearching(true);
    setResults([]);
    setSearchMessage("正在搜索");
    setMessage("正在搜索");

    const searchToken = searchTokenRef.current + 1;
    searchTokenRef.current = searchToken;
    const abortController = new AbortController();
    currentSearchAbortControllerRef.current = abortController;
    let searchResults: SearchResult[] = [];
    const failureReasons: string[] = [];

    try {
      await streamSearchBooks(user.id, normalizedKeyword, kind, event => {
        if (searchTokenRef.current !== searchToken) return;
        if (event.type === "source") {
          searchResults = mergeSearchResults(searchResults, event.results);
          setResults(searchResults);
          setSearchMessage(`已显示 ${searchResults.length} 本`);
        }

        if (event.type === "failure") {
          failureReasons.push(event.reason);
          setSearchMessage(resolveSearchFailureMessage(failureReasons));
        }
      }, abortController.signal);

      if (searchTokenRef.current !== searchToken) return searchResults;
      setSearchMessage(resolveSearchDoneMessage(searchResults.length, failureReasons));
      return searchResults;
    } catch (error) {
      if (abortController.signal.aborted || isAbortError(error)) {
        if (searchTokenRef.current === searchToken) setSearchMessage("搜索已停止");
        return searchResults;
      }

      setSearchMessage(error instanceof Error ? error.message : String(error));
      return [];
    } finally {
      if (searchTokenRef.current === searchToken) {
        currentSearchAbortControllerRef.current = null;
        setSearching(false);
        setBusy(false);
      }
    }
  }

  async function handleSearch(event?: FormEvent<HTMLFormElement>) {
    event?.preventDefault();
    await searchByKeyword(query);
  }

  function handleSourceSelect(bookId: string, sourceId: string) {
    setSelectedSourceByBook(current => ({
      ...current,
      [bookId]: sourceId,
    }));
  }

  function patchBookshelfActiveSource(bookId: string, sourceId: string) {
    setBookshelf(current =>
      sortBookshelfView(current.map(item => (item.book.id === bookId ? { ...item, item: { ...item.item, activeSourceId: sourceId } } : item))),
    );
  }

  function patchBookshelfGroup(bookId: string, groupId: string) {
    setBookshelf(current =>
      sortBookshelfView(current.map(item => (item.book.id === bookId ? { ...item, item: { ...item.item, groupId } } : item))),
    );
  }

  function patchBookshelfProgress(bookId: string, progress: NonNullable<BookshelfViewItem["progress"]>) {
    setBookshelf(current =>
      sortBookshelfView(
        current.map(item => (item.book.id === bookId ? { ...item, progress } : item)),
      ),
    );
  }

  function removeBookshelfItem(bookId: string) {
    setBookshelf(current => current.filter(item => item.book.id !== bookId));
    setSelectedSourceByBook(current => {
      if (!(bookId in current)) return current;
      const next = { ...current };
      delete next[bookId];
      return next;
    });
  }

  function handleQueryChange(value: string) {
    setQuery(value);
    if (!value.trim()) {
      setResults([]);
      setSearchMessage("输入书名或作者后开始搜索");
    }
  }

  function handleClearSearchResults() {
    setResults([]);
    setSearchMessage("输入书名或作者后开始搜索");
    setMessage("已返回书架");
  }

  function resetSearchResults() {
    setResults([]);
    setSearchMessage("输入书名或作者后开始搜索");
  }

  function handleRemoveSearchResult(bookId: string) {
    setResults(current => current.filter(result => result.book.id !== bookId));
    setSearchMessage("已从搜索结果移除");
  }

  function handleStopSearch() {
    abortCurrentSearch({ invalidate: true, showMessage: true });
  }

  function abortCurrentSearch({ invalidate, showMessage }: { invalidate: boolean; showMessage: boolean }) {
    const current = currentSearchAbortControllerRef.current;

    if (invalidate) searchTokenRef.current += 1;
    currentSearchAbortControllerRef.current = null;
    current?.abort();
    setSearching(false);
    setBusy(false);
    if (showMessage) setSearchMessage("搜索已停止");
  }

  return {
    bookshelf,
    query,
    results,
    searchMessage,
    searching,
    selectedSourceByBook,
    setBookshelf,
    setResults,
    loadBookshelf,
    patchBookshelfActiveSource,
    patchBookshelfGroup,
    patchBookshelfProgress,
    removeBookshelfItem,
    searchByKeyword,
    handleSearch,
    handleSourceSelect,
    handleQueryChange,
    handleClearSearchResults,
    resetSearchResults,
    handleRemoveSearchResult,
    handleStopSearch,
  };
}

function mergeSearchResults(current: SearchResult[], incoming: SearchResult[]): SearchResult[] {
  const results = new Map(current.map(result => [result.book.id, { ...result, sources: [...result.sources] }]));

  // 流式搜索会多次返回同一本书，按 bookId 合并来源
  for (const result of incoming) {
    const existing = results.get(result.book.id);
    if (!existing) {
      results.set(result.book.id, { ...result, sources: [...result.sources] });
      continue;
    }

    existing.book = result.book;
    existing.sources = mergeBookSources(existing.sources, result.sources);
  }

  return [...results.values()];
}

function mergeBookSources(current: BookSource[], incoming: BookSource[]): BookSource[] {
  const sources = new Map(current.map(source => [buildBookSourceKey(source), source]));

  for (const source of incoming) {
    sources.set(buildBookSourceKey(source), source);
  }

  return [...sources.values()];
}

function buildBookSourceKey(source: BookSource): string {
  return `${source.sourceId}:${source.sourceBookId}`;
}

function resolveSearchDoneMessage(visibleCount: number, failureReasons: string[] = []): string {
  if (visibleCount > 0) return `找到 ${visibleCount} 本书`;
  if (failureReasons.length > 0) return resolveSearchFailureMessage(failureReasons);
  return "没有新的搜索结果";
}

function resolveSearchFailureMessage(failureReasons: string[]): string {
  const latestReason = failureReasons.at(-1);
  return latestReason ? `搜索失败：${latestReason}` : "搜索失败";
}

function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === "AbortError";
}
